import { UI } from "../constants/uiText";

const RAIN_CODES = [51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82];
const SNOW_CODES = [71, 73, 75, 77, 85, 86];
const THUNDER_CODES = [95, 96, 99];
const FOG_CODES = [45, 48];
const PARTLY_CLOUDY_CODES = [1, 2];
const CLOUDY_CODES = [3];
const SLEET_CODES = [56, 57, 66, 67];

export const getWeatherLabel = (code, lang) => {
  return UI[lang]?.weather?.[code] || UI[lang].unknown;
};

export const getWeatherIcon = (code, isDay = 1) => {
  if (code === 0) return isDay ? "☀️" : "🌙";
  if (code === 1) return isDay ? "🌤️" : "🌙";
  if (code === 2) return "⛅";
  if (code === 3) return "☁️";
  if (FOG_CODES.includes(code)) return "🌫️";
  if (RAIN_CODES.includes(code)) return "🌧️";
  if (SNOW_CODES.includes(code)) return "❄️";
  if (THUNDER_CODES.includes(code)) return "⛈️";
  return "☁️";
};

export const mapWeatherCodeToSceneType = (code, windSpeed = 0) => {
  if (THUNDER_CODES.includes(code)) return "thunderstorm";
  if (FOG_CODES.includes(code)) return "fog";
  if (SLEET_CODES.includes(code)) return "sleet";
  if (SNOW_CODES.includes(code)) return "snow";
  if (RAIN_CODES.includes(code)) return "rain";
  if (windSpeed >= 35) return "wind";
  if (PARTLY_CLOUDY_CODES.includes(code)) return "partly-cloudy";
  if (CLOUDY_CODES.includes(code)) return "cloudy";
  return "clear";
};

export const formatHour = (dateString, index, lang) => {
  if (index === 0) return UI[lang].now;

  return new Date(dateString).toLocaleTimeString(
    lang === "ru" ? "ru-RU" : "ro-RO",
    {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }
  );
};

export const formatDay = (dateString, index, lang) => {
  if (index === 0) return UI[lang].today;

  return new Date(dateString).toLocaleDateString(
    lang === "ru" ? "ru-RU" : "ro-RO",
    {
      weekday: "short",
    }
  );
};

export const getWeatherAdvice = (weather, lang) => {
  if (!weather || !weather.hourly?.length) return "";

  const rainyHour = weather.hourly.find(
    (hour, index) => index > 0 && hour.rain && hour.rain >= 40
  );
  const heavyRainHour = weather.hourly.find(
    (hour, index) => index > 0 && hour.rain && hour.rain >= 70
  );

  const hotHours = weather.hourly.filter((hour) => hour.temp >= 28);
  const coldHours = weather.hourly.filter((hour) => hour.temp <= 5);

  if (heavyRainHour) return UI[lang].advice.heavyRain(heavyRainHour.time);
  if (rainyHour) return UI[lang].advice.rain(rainyHour.time);

  if (weather.currentTemp >= 30 || hotHours.length >= 3) {
    return UI[lang].advice.hot;
  }

  if (weather.currentTemp >= 22 && weather.currentTemp <= 27) {
    return UI[lang].advice.perfect;
  }

  if (weather.currentTemp >= 12 && weather.currentTemp <= 21) {
    return UI[lang].advice.mild;
  }

  if (coldHours.length >= 3 || weather.currentTemp < 10) {
    return UI[lang].advice.cold;
  }

  if (
    weather.description?.toLowerCase().includes("fog") ||
    weather.sceneWeather === "fog"
  ) {
    return UI[lang].advice.fog;
  }

  if (
    weather.description?.toLowerCase().includes("storm") ||
    weather.description?.toLowerCase().includes("гроза") ||
    weather.sceneWeather === "thunderstorm"
  ) {
    return UI[lang].advice.storm;
  }

  return UI[lang].advice.calm;
};

export const getExtraAdvice = (weather, lang) => {
  if (!weather || !weather.hourly?.length) return "";

  const sunnyHours = weather.hourly.filter((hour) =>
    ["☀️", "🌤️", "⛅"].includes(hour.icon)
  ).length;

  const rainyHours = weather.hourly.filter(
    (hour) => hour.rain && hour.rain >= 30
  ).length;

  if (sunnyHours >= 8 && weather.high >= 20) {
    return UI[lang].advice.walkDrive;
  }

  if (rainyHours >= 4) {
    return UI[lang].advice.wet;
  }

  if (weather.high <= 8) {
    return UI[lang].advice.chilly;
  }

  return UI[lang].advice.balanced;
};

export const getDistance = (lat1, lon1, lat2, lon2) => {
  const toRad = (value) => (value * Math.PI) / 180;
  const earthRadiusKm = 6371;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return earthRadiusKm * c;
};

export const getNearestCity = (lat, lon, cities, defaultCity) => {
  return cities.reduce((closest, city) => {
    const currentDistance = getDistance(lat, lon, city.lat, city.lon);
    const closestDistance = getDistance(
      lat,
      lon,
      closest.lat,
      closest.lon
    );

    return currentDistance < closestDistance ? city : closest;
  }, defaultCity);
};

export const getTimeOfDay = (currentTime, sunriseTime, sunsetTime) => {
  const current = new Date(currentTime).getTime();
  const sunrise = new Date(sunriseTime).getTime();
  const sunset = new Date(sunsetTime).getTime();

  const sunriseStart = sunrise - 45 * 60 * 1000;
  const sunriseEnd = sunrise + 60 * 60 * 1000;

  const sunsetStart = sunset - 60 * 60 * 1000;
  const sunsetEnd = sunset + 45 * 60 * 1000;

  if (current >= sunriseStart && current <= sunriseEnd) return "sunrise";
  if (current >= sunsetStart && current <= sunsetEnd) return "sunset";
  if (current < sunriseStart || current > sunsetEnd) return "night";

  return "day";
};